import React, { useState, useRef, useEffect } from 'react';
import { X, Phone, MapPin, Calendar, FileText, DollarSign, User, Upload, Trash2, Image as ImageIcon, FileText as FileTextIcon, Save, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDDMMYYYY } from '../../utils/dateI18n';
import { traduzirMetodoPagamento } from '../../shared/logica/pagamentos';
import { compressImages } from '../../utils/imageUtils';

function DetailRow({ label, value, icon: Icon }) {
  if (!value || value === '') return null;
  
  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-slate-400" />}
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</span>
      </div>
      <span className="text-lg font-bold text-slate-800">{value}</span>
    </div>
  );
}

function DetailSection({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-widest">{title}</h3>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

/**
 * Modal para ver/editar detalhes de um registo AC/Painel Solar
 */
export default function ModalDetalhesACPanel({ 
  record, 
  onClose, 
  onUpdate, 
  currentUser 
}) {
  const [photos, setPhotos] = useState(record?.photos || []);
  const [notes, setNotes] = useState(record?.notes || '');
  const [selectedPhoto, setSelectedPhoto] = useState(null); // Para lightbox de fotos
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0); // Índice da foto selecionada
  const [selectedPhotoArray, setSelectedPhotoArray] = useState([]); // Array de fotos para navegação
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (record) {
      setPhotos(record.photos || []);
      setNotes(record.notes || '');
    }
  }, [record]);

  if (!record) return null;

  const handlePhotoUpload = async (files) => {
    try {
      // Comprimir imagens antes de converter para base64
      // maxWidth: 1920, maxHeight: 1920, quality: 0.8, maxSizeKB: 400 (para garantir que fica abaixo do limite)
      const compressedPhotos = await compressImages(files, 1920, 1920, 0.8, 400);
      const updatedPhotos = [...photos, ...compressedPhotos].slice(0, 20); // Limite de 20 fotos
      setPhotos(updatedPhotos);
      
      // Atualizar no servidor - garantir que photos seja sempre um array
      if (onUpdate) {
        await onUpdate({ 
          ...record, 
          photos: Array.isArray(updatedPhotos) ? updatedPhotos : [] 
        });
      }
    } catch (error) {
      console.error('Erro ao comprimir/upload fotos:', error);
      // Em caso de erro, tentar sem compressão (fallback)
      const arr = Array.from(files || []);
      const readers = arr.map(file => new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(file);
      }));
      const newPhotos = await Promise.all(readers);
      const updatedPhotos = [...photos, ...newPhotos].slice(0, 20);
      setPhotos(updatedPhotos);
      
      if (onUpdate) {
        await onUpdate({ 
          ...record, 
          photos: Array.isArray(updatedPhotos) ? updatedPhotos : [] 
        });
      }
    }
  };

  const handleRemovePhoto = async (index) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);
    setPhotos(updatedPhotos);
    
    // Atualizar no servidor - garantir que photos seja sempre um array
    if (onUpdate) {
      await onUpdate({ 
        ...record, 
        photos: Array.isArray(updatedPhotos) ? updatedPhotos : [] 
      });
    }
  };

  const handleSaveNotes = async () => {
    if (onUpdate) {
      await onUpdate({ ...record, notes });
    }
  };

  const handleSave = async () => {
    if (onUpdate) {
      // Garantir que photos seja sempre um array válido
      await onUpdate({ 
        ...record, 
        notes, 
        photos: Array.isArray(photos) ? photos : [] 
      });
      // Fechar o modal após guardar
      onClose();
    }
  };

  // Construir endereço completo
  const addressParts = [
    record.street,
    record.doorNumber,
    record.locality,
    record.zipCode
  ].filter(Boolean);
  const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[80] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-[3rem] w-full max-w-3xl shadow-2xl p-8 md:p-10 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-8">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-3xl font-black text-slate-900 leading-none">
                {record.client || '—'}
              </h2>
              {record.recordType === 'BUDGET' && (
                <span className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-xl text-xs font-black uppercase flex items-center gap-1.5">
                  <FileTextIcon size={14} />
                  Orçamento
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {record.date && (
                <div className="flex items-center gap-2 text-green-600 font-bold uppercase text-[10px] tracking-widest">
                  <Calendar size={12} />
                  <span>{formatDDMMYYYY(new Date(record.date))}</span>
                </div>
              )}
              {record.value && (
                <div className="flex items-center gap-2 text-green-600 font-bold uppercase text-[10px] tracking-widest">
                  <DollarSign size={12} />
                  <span>{Number(record.value).toFixed(2)}€</span>
                </div>
              )}
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 bg-slate-50 rounded-full hover:bg-slate-100 transition-colors flex-shrink-0"
            aria-label="Fechar"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-8">
          {/* Informações do Cliente */}
          <DetailSection title="Informações do Cliente">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
              <DetailRow label="Nome" value={record.client} icon={User} />
              <DetailRow label="Telefone" value={record.phone} icon={Phone} />
              {fullAddress && (
                <DetailRow label="Morada" value={fullAddress} icon={MapPin} />
              )}
            </div>
          </DetailSection>

          {/* Produtos/Serviços */}
          {record.products && Array.isArray(record.products) && record.products.length > 0 && (
            <DetailSection title="Produtos / Serviços">
              <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-3">
                {record.products.map((prod, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-4 p-3 bg-white rounded-xl border border-slate-200">
                    <div className="flex-1">
                      <div className="font-black text-slate-900 text-lg">{prod.name || '—'}</div>
                      {prod.ref && (
                        <div className="text-sm text-slate-600 font-bold mt-1">
                          Ref: {prod.ref}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </DetailSection>
          )}

          {/* Informações Financeiras */}
          <DetailSection title="Informações Financeiras">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
              <DetailRow 
                label="Valor Total" 
                value={record.value ? `${Number(record.value).toFixed(2)}€` : '—'} 
                icon={DollarSign} 
              />
              <DetailRow 
                label="Método de Pagamento" 
                value={record.paymentMethod ? traduzirMetodoPagamento(record.paymentMethod) : '—'} 
                icon={FileText} 
              />
              {record.invoiceNumber && record.invoiceStatus === 'FATURADO' && (
                <DetailRow label="Nº Fatura" value={record.invoiceNumber} icon={FileText} />
              )}
              {record.invoiceStatus && (
                <div className="flex flex-col">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-slate-400" />
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Estado Faturação</span>
                  </div>
                  <span className="text-lg font-bold text-slate-800">
                    {record.invoiceStatus === 'FATURADO' ? 'Faturado' : 
                     record.invoiceStatus === 'PARA_FATURAR' ? 'Para Faturar' : 
                     'Não Faturar'}
                  </span>
                </div>
              )}
            </div>
          </DetailSection>

          {/* Notas */}
          <DetailSection title="Notas">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleSaveNotes}
                placeholder="Adicionar notas..."
                className="w-full p-3 bg-white border border-slate-200 rounded-xl font-bold text-slate-900 min-h-[100px] resize-y focus:outline-none focus:border-green-600"
              />
            </div>
          </DetailSection>

          {/* Fotos */}
          <DetailSection title="Fotos">
            <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-4">
              {/* Botão para adicionar fotos */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-slate-600 font-bold">
                  <ImageIcon size={18} />
                  <span>{photos.length} {photos.length === 1 ? 'foto' : 'fotos'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-green-600 text-white rounded-xl font-black text-sm hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                  <Upload size={16} />
                  Adicionar Fotos
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handlePhotoUpload(e.target.files)}
                  className="hidden"
                />
              </div>

              {/* Grid de fotos */}
              {photos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img
                        src={photo}
                        alt={`Foto ${idx + 1}`}
                        onClick={() => {
                          setSelectedPhoto(photo);
                          setSelectedPhotoIndex(idx);
                          setSelectedPhotoArray(photos);
                        }}
                        className="w-full h-40 object-cover rounded-xl border-2 border-slate-200 cursor-pointer hover:opacity-90 transition-opacity"
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemovePhoto(idx);
                        }}
                        className="absolute top-2 right-2 p-2 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700 z-10"
                        title="Remover foto"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DetailSection>

          {/* Botões de ação */}
          <div className="flex gap-4 pt-4 border-t border-slate-200">
            <button
              onClick={handleSave}
              className="px-6 py-3 bg-green-600 text-white rounded-xl font-black hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <Save size={18} />
              Guardar
            </button>
            <button
              onClick={onClose}
              className="ml-auto px-6 py-3 bg-slate-200 text-slate-700 rounded-xl font-black hover:bg-slate-300 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>

      {/* Lightbox para visualizar fotos em tamanho maior */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/95 backdrop-blur-md z-[160] flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Botão fechar */}
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-10 transition-colors"
              title="Fechar"
            >
              <X size={24} />
            </button>

            {/* Navegação anterior */}
            {selectedPhotoArray && selectedPhotoArray.length > 1 && selectedPhotoIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const prevIndex = selectedPhotoIndex - 1;
                  setSelectedPhotoIndex(prevIndex);
                  setSelectedPhoto(selectedPhotoArray[prevIndex]);
                }}
                className="absolute left-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-10 transition-colors"
                title="Foto anterior"
              >
                <ChevronLeft size={24} />
              </button>
            )}

            {/* Imagem em tamanho maior */}
            <img
              src={selectedPhoto}
              alt={`Foto ${selectedPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />

            {/* Navegação próxima */}
            {selectedPhotoArray && selectedPhotoArray.length > 1 && selectedPhotoIndex < selectedPhotoArray.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  const nextIndex = selectedPhotoIndex + 1;
                  setSelectedPhotoIndex(nextIndex);
                  setSelectedPhoto(selectedPhotoArray[nextIndex]);
                }}
                className="absolute right-4 bg-white/20 hover:bg-white/30 text-white rounded-full p-3 z-10 transition-colors"
                title="Próxima foto"
              >
                <ChevronRight size={24} />
              </button>
            )}

            {/* Contador de fotos */}
            {selectedPhotoArray && selectedPhotoArray.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-bold">
                {selectedPhotoIndex + 1} / {selectedPhotoArray.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

